import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { filter, catchError, map } from 'rxjs/operators';
import { AuthUser } from '@/types/task';
import { authConfig } from '../../../config/auth.config';
import { environment } from 'src/environments/environment';

const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,jobTitle,department,employeeType,manager';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private httpClient = inject(HttpClient);
    
    // Use environment-specific API base. In production, AzureFunctionsService or global config provides the external URL.
    private baseUrl = environment.apiUrl;
    
    private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();
    
    private loginDisplaySubject = new BehaviorSubject<boolean>(false);
    public isLoggedIn$ = this.loginDisplaySubject.asObservable();

    private authErrorSubject = new BehaviorSubject<string | null>(null);
    public authError$ = this.authErrorSubject.asObservable();

    private authLoadingSubject = new BehaviorSubject<boolean>(true);
    public authLoading$ = this.authLoadingSubject.asObservable();

    private msalInitialized = false;
    private initializationPromise: Promise<void> | null = null;

    constructor() {
        // Don't initialize immediately - wait for MSAL to be ready
        
    }

    // Public method to initialize the service after MSAL is ready
    async initialize(): Promise<void> {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = this.initializeAuthenticationState();
        return this.initializationPromise;
    }

    private async initializeAuthenticationState(): Promise<void> {
        if (authConfig.useMockAuth) {
            this.initializeMockAuth();
            return;
        }

        try {
            // Wait for MSAL to be initialized
            await this.waitForMsalInitialization();
            
            // Now safely check existing auth state
            this.checkExistingAuthState();
            // Initialize MSAL event listeners
            this.initializeRealAuth();
            
            this.msalInitialized = true;
        } catch (error) {
            console.error('Failed to initialize authentication:', error);
            this.authLoadingSubject.next(false);
        }
    }

    private async waitForMsalInitialization(): Promise<void> {
        const maxWaitTime = 5000; // 5 seconds max wait
        const checkInterval = 100; // Check every 100ms
        let waitTime = 0;

        return new Promise((resolve, reject) => {
            const checkMsal = () => {
                try {
                    // Try to access MSAL instance
                    if (this.msalService?.instance && this.msalService.instance.getConfiguration) {
                        const config = this.msalService.instance.getConfiguration();
                        if (config) {
                            
                            resolve();
                            return;
                        }
                    }
                } catch (error) {
                    // MSAL not ready yet
                }

                waitTime += checkInterval;
                if (waitTime >= maxWaitTime) {
                    reject(new Error('MSAL initialization timeout'));
                } else {
                    setTimeout(checkMsal, checkInterval);
                }
            };

            checkMsal();
        });
    }

    private checkExistingAuthState(): void {
        if (!this.isMsalReady()) {
            
            this.authLoadingSubject.next(false);
            return;
        }

        try {
            
            const accounts = this.msalService.instance.getAllAccounts();
            
            if (accounts && accounts.length > 0) {
                
                // User has existing session, set as logged in immediately
                this.msalService.instance.setActiveAccount(accounts[0]);
                this.loginDisplaySubject.next(true);
                this.authLoadingSubject.next(false);
                this.loadUserProfile();
                return;
            }
            
            
        } catch (error) {
            
        }
        
        // No existing auth found, continue with normal flow
        this.authLoadingSubject.next(false);
    }

    private isMsalReady(): boolean {
        try {
            return !!(this.msalService?.instance && 
                     this.msalService.instance.getConfiguration && 
                     this.msalService.instance.getConfiguration());
        } catch (error) {
            return false;
        }
    }

    private initializeMockAuth() {
        // Mock authentication for development
        const mockUser: AuthUser = {
            id: 'user-001',
            displayName: 'John Doe',
            email: 'john.doe@company.com',
            department: 'Information Technology',
            jobTitle: 'Senior Developer',
            manager: 'jane.smith@company.com',
            role: 'Manager', // Change to 'Employee' to test employee view
            avatar: 'amyelsner.png',
            office365Id: '12345-67890-abcdef'
        };

        this.currentUserSubject.next(mockUser);
        this.loginDisplaySubject.next(true);
        this.authLoadingSubject.next(false);
    }

    private initializeRealAuth() {
        // Set up MSAL listeners without delay if auth state not already determined
        if (!this.loginDisplaySubject.value) {
            
            
            // Listen for authentication status changes
            this.msalBroadcastService.inProgress$
                .pipe(
                    filter((status: InteractionStatus) => status === InteractionStatus.None)
                )
                .subscribe(() => {
                    
                    this.checkAndSetActiveAccount();
                });

            // Listen for login success/failure events
            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) => 
                        msg.eventType === EventType.LOGIN_SUCCESS || 
                        msg.eventType === EventType.LOGIN_FAILURE ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                    )
                )
                .subscribe((result: EventMessage) => {
                    
                    if (result.eventType === EventType.LOGIN_SUCCESS || 
                        result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                        this.checkAndSetActiveAccount();
                    } else if (result.eventType === EventType.LOGIN_FAILURE || 
                               result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                        console.error('Authentication failed:', result);
                        this.setAuthError('Authentication failed. Please try logging in again.');
                        this.authLoadingSubject.next(false);
                    }
                });
            
            // Set up token refresh monitoring
            this.setupTokenRefresh();
        }
    }

    private checkAndSetActiveAccount() {
        if (!this.isMsalReady()) {
            
            return;
        }

        try {
            
            const activeAccount = this.msalService.instance.getActiveAccount();
            
            if (activeAccount) {
                
                this.loginDisplaySubject.next(true);
                this.authLoadingSubject.next(false);
                this.loadUserProfile();
            } else {
                // Check if there are any accounts in cache
                const accounts = this.msalService.instance.getAllAccounts();
                
                
                if (accounts.length > 0) {
                    // Set the first account as active
                    this.msalService.instance.setActiveAccount(accounts[0]);
                    
                    this.loginDisplaySubject.next(true);
                    this.authLoadingSubject.next(false);
                    this.loadUserProfile();
                } else {
                    
                    this.loginDisplaySubject.next(false);
                    this.authLoadingSubject.next(false);
                    this.currentUserSubject.next(null);
                }
            }
        } catch (error) {
            console.error('Error checking account:', error);
            this.loginDisplaySubject.next(false);
            this.authLoadingSubject.next(false);
            this.currentUserSubject.next(null);
        }
    }

    private setupTokenRefresh(): void {
        // Check token expiry every 5 minutes and refresh if needed
        setInterval(() => {
            this.refreshTokenIfNeeded();
        }, 5 * 60 * 1000); // 5 minutes
    }

    private async refreshTokenIfNeeded(): Promise<void> {
        if (!this.isMsalReady()) {
            console.warn('MSAL not initialized, skipping token refresh');
            return;
        }

        const account = this.msalService.instance.getActiveAccount();
        if (!account) return;

        try {
            const silentRequest: SilentRequest = {
                scopes: ['User.Read'],
                account: account,
                forceRefresh: false // Only refresh if token is close to expiry
            };

            await this.msalService.instance.acquireTokenSilent(silentRequest);
            
        } catch (error) {
            console.error('Token refresh failed:', error);
            // If silent refresh fails, user needs to log in again
            this.logout();
        }
    }

    private async loadUserProfile() {
        if (!this.isMsalReady()) {
            console.warn('MSAL not initialized, skipping user profile load');
            return;
        }
        
        const account = this.msalService.instance.getActiveAccount();
        if (!account) return;

        try {
            // Get access token for Microsoft Graph
            const silentRequest: SilentRequest = {
                scopes: ['User.Read'],
                account: account
            };

            const response = await this.msalService.instance.acquireTokenSilent(silentRequest);
            
            // Fetch user profile from Microsoft Graph
            this.httpClient.get(GRAPH_ENDPOINT, {
                headers: {
                    'Authorization': `Bearer ${response.accessToken}`
                }
            }).pipe(
                map((profile: any) => this.mapGraphUserToAuthUser(profile, account)),
                catchError(error => {
                    console.error('Error loading user profile:', error);
                    return of(this.createFallbackUser(account));
                })
            ).subscribe(async (user) => {
                // Update the current user first
                this.currentUserSubject.next(user);
                
                // Send the Graph API profile data to Azure Functions for database update
                if (user.employeeType) {
                    try {
                        
                        const profileData = {
                            id: user.id,
                            displayName: user.displayName,
                            email: user.email,
                            department: user.department,
                            jobTitle: user.jobTitle,
                            employeeType: user.employeeType,
                            manager: user.manager
                        };
                        
                        const result = await this.updateUserProfileInDatabase(profileData);
                        
                        
                        // Update user role if it changed based on database determination
                        if (result.user && result.user.role !== user.role) {
                            const updatedUser = { ...user, role: result.user.role };
                            this.currentUserSubject.next(updatedUser);
                            
                        }
                    } catch (error) {
                        console.error('❌ Error updating profile in database:', error);
                        // Continue with the Graph API user data even if database update fails
                    }
                }
            });

        } catch (error) {
            console.error('Error acquiring token:', error);
            // Fallback to account info
            const fallbackUser = this.createFallbackUser(account);
            this.currentUserSubject.next(fallbackUser);
        }
    }

    private mapGraphUserToAuthUser(profile: any, account: any): AuthUser {
        // Log the complete profile for debugging
        
        
        // Determine role based on employeeType first, then fallback to job title or department
        const role = this.determineUserRoleFromProfile(profile);

        const user: AuthUser = {
            id: profile.id || account.homeAccountId,
            displayName: profile.displayName || account.name || 'Unknown User',
            email: profile.mail || profile.userPrincipalName || account.username,
            department: profile.department || '',
            jobTitle: profile.jobTitle || '',
            employeeType: profile.employeeType || '', // Store the raw employeeType from Graph API
            manager: profile.manager?.mail || '',
            role: role,
            avatar: this.generateAvatar(profile.displayName || account.name),
            office365Id: profile.id || account.localAccountId
        };

        
        return user;
    }

    private createFallbackUser(account: any): AuthUser {
        const role = this.determineUserRole('', '');
        
        return {
            id: account.homeAccountId,
            displayName: account.name || 'Unknown User',
            email: account.username,
            department: 'Unknown Department',
            jobTitle: 'Unknown Title',
            manager: '',
            role: role,
            avatar: this.generateAvatar(account.name),
            office365Id: account.localAccountId
        };
    }

    private determineUserRole(jobTitle: string = '', department: string = ''): 'Manager' | 'Employee' {
        // Customize this logic based on your organization's role determination
        const managerTitles = ['manager', 'director', 'lead', 'supervisor', 'head', 'chief', 'vp', 'vice president'];
        const title = jobTitle.toLowerCase();
        
        return managerTitles.some(managerTitle => title.includes(managerTitle)) ? 'Manager' : 'Employee';
    }

    private determineUserRoleFromProfile(profile: any): 'Manager' | 'Employee' {
        
        
        
        

        // Priority 1: Check employeeType field from Graph API
        if (profile.employeeType) {
            const employeeType = profile.employeeType.toLowerCase().trim();
            
            
            if (employeeType === 'manager' || employeeType.includes('manager')) {
                
                return 'Manager';
            } else if (employeeType === 'employee' || employeeType.includes('employee')) {
                
                return 'Employee';
            }
        }

        // Priority 2: Fallback to job title and department
        
        return this.determineUserRole(profile.jobTitle, profile.department);
    }

    private generateAvatar(displayName: string = ''): string {
        // Generate a default avatar filename based on name
        const avatars = ['amyelsner.png', 'annafali.png', 'ionibowcher.png', 'onyamalimba.png', 'stephenshaw.png'];
        const index = displayName.length % avatars.length;
        return avatars[index];
    }

    // Public authentication methods
    async loginWithOffice365(): Promise<AuthUser | null> {
        if (authConfig.useMockAuth) {
            // Mock login for development
            this.initializeMockAuth();
            return this.currentUserSubject.value;
        }

        if (!this.isMsalReady()) {
            console.error('MSAL not initialized for login');
            this.setAuthError('Authentication system not ready. Please refresh the page.');
            return null;
        }

        try {
            const loginRequest: PopupRequest = {
                scopes: ['User.Read', 'profile', 'openid', 'email'],
                prompt: 'select_account'
            };

            const response = await this.msalService.loginPopup(loginRequest).toPromise();
            
            if (response?.account) {
                this.msalService.instance.setActiveAccount(response.account);
                await this.loadUserProfile();
                return this.currentUserSubject.value;
            }
            
            return null;
        } catch (error) {
            console.error('Office365 login failed:', error);
            this.setAuthError('Login failed. Please check your credentials and try again.');
            return null;
        }
    }

    async loginRedirect(): Promise<void> {
        if (authConfig.useMockAuth) {
            this.initializeMockAuth();
            return;
        }

        if (!this.isMsalReady()) {
            console.warn('MSAL not initialized, waiting for initialization...');
            await this.waitForMsalInitialization();
        }

        const loginRequest: RedirectRequest = {
            scopes: ['User.Read', 'profile', 'openid', 'email']
        };

        this.msalService.loginRedirect(loginRequest);
    }

    async logout(): Promise<void> {
        if (authConfig.useMockAuth) {
            this.currentUserSubject.next(null);
            this.loginDisplaySubject.next(false);
            return;
        }

        if (!this.isMsalReady()) {
            console.warn('MSAL not initialized, performing local logout only');
            this.currentUserSubject.next(null);
            this.loginDisplaySubject.next(false);
            return;
        }

        const account = this.msalService.instance.getActiveAccount();
        if (account) {
            this.msalService.logoutPopup({
                account: account,
                postLogoutRedirectUri: authConfig.msalConfig.auth.postLogoutRedirectUri
            });
        }
        
        this.currentUserSubject.next(null);
        this.loginDisplaySubject.next(false);
    }

    getCurrentUser(): AuthUser | null {
        return this.currentUserSubject.value;
    }

    isManager(): boolean {
        return this.currentUserSubject.value?.role === 'Manager';
    }

    isEmployee(): boolean {
        return this.currentUserSubject.value?.role === 'Employee';
    }

    isLoggedIn(): boolean {
        return this.loginDisplaySubject.value;
    }

    // Development/testing methods - only work in mock mode
    switchToEmployeeMode(): void {
        if (authConfig.useMockAuth) {
            const mockEmployee: AuthUser = {
                id: 'user-002',
                displayName: 'Jane Smith',
                email: 'jane.smith@company.com',
                department: 'Information Technology',
                jobTitle: 'Developer',
                employeeType: 'Employee',
                manager: 'john.doe@company.com',
                role: 'Employee',
                avatar: 'annafali.png',
                office365Id: '67890-12345-fedcba'
            };
            this.currentUserSubject.next(mockEmployee);
        }
    }

    switchToManagerMode(): void {
        if (authConfig.useMockAuth) {
            const mockManager: AuthUser = {
                id: 'user-001',
                displayName: 'John Doe',
                email: 'john.doe@company.com',
                department: 'Information Technology',
                jobTitle: 'Senior Developer',
                employeeType: 'Manager',
                manager: 'jane.smith@company.com',
                role: 'Manager',
                avatar: 'amyelsner.png',
                office365Id: '12345-67890-abcdef'
            };
            this.currentUserSubject.next(mockManager);
        }
    }

    // Token management methods
    async getAccessToken(): Promise<string | null> {
        if (authConfig.useMockAuth) {
            // Return a mock JWT token for development
            return await this.generateMockJwtToken();
        }

        if (!this.isMsalReady()) {
            console.error('MSAL not initialized for token acquisition');
            return null;
        }

        try {
            const account = this.msalService.instance.getActiveAccount();
            if (!account) {
                console.error('No active account for token acquisition');
                return null;
            }

            const silentRequest: SilentRequest = {
                scopes: ['User.Read', 'profile', 'openid', 'email'], // Standard Microsoft Graph scopes
                account: account
            };

            const response = await this.msalService.acquireTokenSilent(silentRequest).toPromise();
            return response?.accessToken || null;
        } catch (error) {
            console.error('Error acquiring access token:', error);
            return null;
        }
    }

    private async generateMockJwtToken(): Promise<string> {
        // Generate a properly signed mock JWT token for development
        const header = { alg: 'HS256', typ: 'JWT' };
        const payload = {
            sub: this.currentUserSubject.value?.id || 'user-001',
            id: this.currentUserSubject.value?.id || 'user-001', // Also include as 'id' claim
            email: this.currentUserSubject.value?.email || 'john.doe@company.com',
            name: this.currentUserSubject.value?.displayName || 'John Doe',
            role: this.currentUserSubject.value?.role || 'Manager',
            department: this.currentUserSubject.value?.department || 'IT',
            jobTitle: this.currentUserSubject.value?.jobTitle || 'Senior Developer',
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + (60 * 60) // 1 hour expiry
        };

        // Base64URL encode header and payload
        const encodedHeader = this.base64UrlEncode(JSON.stringify(header));
        const encodedPayload = this.base64UrlEncode(JSON.stringify(payload));
        
        // Create signature using HMAC-SHA256 (simplified for development)
        // In a real implementation, you'd use a proper crypto library
        const secretKey = 'letscreateasuppersecreateapikeysecreate@128548s6d@38373984*&&#@#fjjdsklsvhlsdajfadskfjaslfejlfsj_sfjsdkfjksdlfjskd_MF';
        const signature = await this.createSignature(`${encodedHeader}.${encodedPayload}`, secretKey);

        return `${encodedHeader}.${encodedPayload}.${signature}`;
    }

    private base64UrlEncode(str: string): string {
        return btoa(str)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    private async createSignature(data: string, secret: string): Promise<string> {
        // Simple signature for development - in production use proper crypto
        // This creates a basic hash that the Azure Function can validate
        const encoder = new TextEncoder();
        const keyData = encoder.encode(secret);
        const dataToSign = encoder.encode(data);
        
        try {
            const cryptoKey = await crypto.subtle.importKey(
                'raw',
                keyData,
                { name: 'HMAC', hash: 'SHA-256' },
                false,
                ['sign']
            );
            
            const signature = await crypto.subtle.sign('HMAC', cryptoKey, dataToSign);
            const signatureArray = new Uint8Array(signature);
            const signatureBase64 = btoa(String.fromCharCode(...signatureArray));
            
            return this.base64UrlEncode(signatureBase64);
        } catch (error) {
            console.error('Error creating signature:', error);
            // Fallback to a predictable signature for development
            return this.base64UrlEncode(btoa(data + secret));
        }
    }

    // Direct HTTP call to update user profile in database (avoids circular dependency)
    private async updateUserProfileInDatabase(profileData: any): Promise<any> {
        try {
            const token = await this.getAccessToken();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            });

            return this.httpClient.post(`${environment.apiUrl}/UpdateUserProfile`, profileData, { headers }).toPromise();
        } catch (error) {
            console.error('Error updating user profile in database:', error);
            throw error;
        }
    }

    // Error handling methods
    clearAuthError(): void {
        this.authErrorSubject.next(null);
    }

    private setAuthError(error: string): void {
        this.authErrorSubject.next(error);
        console.error('Authentication Error:', error);
    }

    retryLogin(): void {
        this.clearAuthError();
        this.loginWithOffice365();
    }
}

/*
Example Office365 Integration Structure:

npm install @azure/msal-browser @azure/msal-angular

// In app.config.ts:
import { MsalModule, MsalService } from '@azure/msal-angular';

const msalConfig = {
    auth: {
        clientId: 'your-azure-ad-app-client-id',
        authority: 'https://login.microsoftonline.com/your-tenant-id',
        redirectUri: window.location.origin
    },
    cache: {
        cacheLocation: 'localStorage'
    }
};

// Then use MsalService in AuthService to handle real authentication
*/